import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, MapPin, ScanLine } from "lucide-react";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import {
  buildSparePartPrintJobs,
  resolveSparePartLabelSize,
  useLabelPrinter,
  useLabelPrintingSettings,
} from "@/labels";
import { Button } from "@/components/ui/button";
import { useSparePartsByIds } from "@/hooks/useSpareParts";
import { useAllStorageBins } from "@/hooks/useStorageBins";
import { SparePartLabel } from "@/components/SparePartLabel";
import { BinScanner } from "@/components/BinScanner";
import { BinLocationChoiceDialog, type BinLocationChoice } from "@/components/BinLocationChoiceDialog";
import { resolveScannedBin } from "@/lib/binScan";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export default function SparePartLabels() {
  const { t, i18n } = useTranslation("inventory");
  const navigate = useNavigate();
  const { id: applianceId } = useParams();
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: parts = [] } = useSparePartsByIds(ids);
  const { data: bins = [] } = useAllStorageBins();
  const [assigning, setAssigning] = useState<string | null>(null);
  const [scanningPartId, setScanningPartId] = useState<string | null>(null);
  // An old bin label whose code exists in two locations: ask which one (FIX-319).
  const [binChoice, setBinChoice] = useState<BinLocationChoice | null>(null);

  const assignBin = async (partId: string, binId: string) => {
    setAssigning(partId);
    const { error } = await supabase
      .from("spare_parts")
      .update({ bin_id: binId, status: "stored" })
      .eq("id", partId);
    setAssigning(null);
    if (error) {
      toast({ title: t("labels.failedToAssign"), description: error.message, variant: "destructive" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["spare_parts_by_ids", ids.join(",")] });
    queryClient.invalidateQueries({ queryKey: ["spare_parts"] });
    toast({ title: t("labels.storedToast"), description: t("labels.assignedToBin") });
  };

  const handleScannedBin = (partId: string, scanned: string) => {
    const binScan = resolveScannedBin(scanned, bins);
    if (binScan.kind === "none") {
      toast({
        title: t("labels.binNotFound"),
        description: t("labels.binNotFoundDesc", { address: scanned.trim() }),
        variant: "destructive",
      });
      return;
    }
    if (binScan.kind === "bin") assignBin(partId, binScan.bin.id);
    else setBinChoice({ bins: binScan.bins, onPick: (b) => assignBin(partId, b.id) });
  };

  const allPlaced = parts.length > 0 && parts.every((p) => !!p.bin_id);

  const { settings: labelPrintingSettings } = useLabelPrintingSettings();
  const { zebraPrint } = useLabelPrinter();
  const handleZebraPrintAll = async () => {
    const size = resolveSparePartLabelSize(labelPrintingSettings.defaultLabelSize);
    const jobs = buildSparePartPrintJobs(
      parts.map((p) => ({
        id: p.id,
        name: p.name,
        serial_no: p.serial_no,
        qr_token: p.qr_token,
        brand: p.brand,
        model_number: p.model_number,
        harvested_at: p.harvested_at,
      })),
      { size, locale: i18n.language || "en-GB" },
    );
    await zebraPrint(jobs);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6 print:hidden">
        <button onClick={() => navigate(`/appliance/${applianceId}/harvest`)} className="flex items-center gap-1 text-muted-foreground hover:text-foreground mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">{t("labels.backToHarvest")}</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t("labels.title")}</h1>
            <p className="text-muted-foreground">{t("labels.subtitle")}</p>
          </div>
          <div className="flex gap-2">
            <SparePartLabelPrintMenu
              namespace="inventory"
              mode="all"
              onPrintBrowser={() => window.print()}
              onPrintZebra={handleZebraPrintAll}
              disabled={parts.length === 0}
            />
            {allPlaced && (
              <Button onClick={() => navigate(`/appliance/${applianceId}/harvest`)} className="gap-2">
                <Check className="w-4 h-4" /> {t("labels.done")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 space-y-4 print:p-0">
        {parts.length === 0 && (
          <div className="text-center text-muted-foreground py-8">{t("labels.noPartsToDisplay")}</div>
        )}

        {parts.map((p) => {
          const qrUrl = `${window.location.origin}/spare-part/${p.serial_no || p.qr_token}`;
          const placedBin = bins.find((b) => b.id === p.bin_id);
          return (
            <div key={p.id} className="bg-card rounded-xl border border-border p-4 flex flex-col md:flex-row gap-4 items-start print:break-inside-avoid">
              <div className="flex-shrink-0 w-full md:w-[440px]">
                <SparePartLabel
                  qrValue={qrUrl}
                  partName={p.name}
                  partId={p.serial_no || p.qr_token.slice(0, 12).toUpperCase()}
                  sourceBrand={p.brand}
                  sourceModel={p.model_number}
                  harvestedAt={p.harvested_at}
                  qrSize={140}
                />
              </div>
              <div className="flex-1 min-w-0 print:hidden">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground">{p.name}</h3>
                  {p.extraction_status === "damaged" || p.status === "damaged" ? (
                    <span className="px-2 py-0.5 text-xs rounded bg-destructive/10 text-destructive">
                      {t("labels.damaged")}
                    </span>
                  ) : (
                    <span className={`px-2 py-0.5 text-xs rounded ${p.status === "stored" ? "bg-primary/10 text-primary" : "bg-warning/20 text-warning"}`}>
                      {p.status === "stored" ? t("labels.stored") : t("labels.awaitingPlacement")}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {t("labels.sourceLine", {
                    brand: p.brand || "—",
                    model: p.model_number || "",
                    date: new Date(p.harvested_at).toLocaleDateString(),
                  })}
                </p>

                {placedBin ? (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="font-mono font-medium text-foreground">{placedBin.address}</span>
                    <Button variant="ghost" size="sm" onClick={() => assignBin(p.id, "")} disabled>
                      {t("labels.move")}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => setScanningPartId(p.id)}
                        disabled={assigning === p.id}
                        className="gap-2"
                      >
                        <ScanLine className="w-4 h-4" /> {t("labels.scanBin")}
                      </Button>
                      <span className="text-xs text-muted-foreground">{t("labels.or")}</span>
                      <SearchableSelect
                        className="max-w-xs"
                        value={null}
                        onChange={(v) => v && assignBin(p.id, v)}
                        disabled={assigning === p.id}
                        placeholder={t("labels.pickStorageBin")}
                        searchPlaceholder={t("labels.searchBins")}
                        emptyMessage={bins.length === 0 ? t("labels.noBinsConfigured") : t("labels.noBinsMatch")}
                        items={bins.map((b) => ({ value: b.id, label: b.address }))}
                      />

                    </div>
                    <p className="text-xs text-muted-foreground">{t("labels.scanBinHint")}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <BinScanner
        open={!!scanningPartId}
        onOpenChange={(o) => { if (!o) setScanningPartId(null); }}
        onDetected={(text) => {
          if (scanningPartId) handleScannedBin(scanningPartId, text);
          setScanningPartId(null);
        }}
      />

      <BinLocationChoiceDialog choice={binChoice} onClose={() => setBinChoice(null)} />

      <style>{`
        @media print {
          @page { margin: 10mm; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
